import api from './client'

// ---- types --------------------------------------------------------------

export interface ProjectRoleCatalogEntry {
  role: string
  display: string
  description: string
}

export interface ProjectMemberInfo {
  id: number
  user_id: string
  user_name: string
  email: string
  is_active: boolean
  roles: string[]
  role_display: string[]
  added_by: string
  created_at: string | null
  tenant_id?: string | null
  tenant_name?: string
  is_cross_tenant?: boolean
  /** Functional (approver) roles this user actually holds, read from the
   *  database their organization runs the application in. Only populated by
   *  the customer-organizations listing. */
  functional_roles?: string[]
}

export interface TenantInfo {
  tenant_id: string | null
  tenant_name: string
  email_domain: string
  web_site: string
}

export interface ProjectAccessSummary {
  project_id: string
  owner_user_id: string
  is_owner: boolean
  roles: string[]
  permissions: string[]
  // 'all' (owner / manage-team), 'own_tenant' (cross-tenant func-admin), or null.
  manage_team_scope?: 'all' | 'own_tenant' | null
  tenant?: TenantInfo
}

export interface ProjectTenantInfo extends TenantInfo {
  status: 'pending' | 'active' | 'revoked'
  first_user_id: string | null
  db_name: string | null
  db_provisioned_at: string | null
  created_at: string | null
  users: ProjectMemberInfo[]
  user_count: number
}

export interface ProjectInvitationInfo {
  id: number
  email: string
  roles: string[]
  role_display: string[]
  invitation_date: string | null
  expiry_date: string | null
  is_expired: boolean
}

export interface CandidateUser {
  user_id: string
  user_name: string
  email: string
}

export interface FunctionalRoleUser {
  user_id: string
  user_name: string
  email: string
  is_owner: boolean
  functional_roles: string[]
}

// ---- API ----------------------------------------------------------------

export async function getRoleCatalog(): Promise<{ success: boolean; roles: ProjectRoleCatalogEntry[] }> {
  const res = await api.get('/projects/roles/catalog')
  return res.data
}

export async function getMyAccess(projectId: string): Promise<{ success: boolean } & ProjectAccessSummary> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/me`)
  return res.data
}

export async function listMembers(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/members`)
  return res.data as {
    success: boolean
    owner: { user_id: string; user_name: string; email: string; tenant_id?: string | null; tenant_name?: string }
    members: ProjectMemberInfo[]
  }
}

export async function listProjectInvitations(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/invitations`)
  return res.data as { success: boolean; invitations: ProjectInvitationInfo[] }
}

export async function listCandidateUsers(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/candidates`)
  return res.data as { success: boolean; users: CandidateUser[] }
}

export async function addMember(projectId: string, userId: string, roles: string[]) {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/team/members`, {
    user_id: userId,
    roles,
  })
  return res.data
}

export async function updateMemberRoles(projectId: string, memberId: number, roles: string[]) {
  const res = await api.patch(
    `/projects/${encodeURIComponent(projectId)}/team/members/${memberId}/roles`,
    { roles },
  )
  return res.data
}

export async function removeMember(projectId: string, memberId: number) {
  const res = await api.delete(`/projects/${encodeURIComponent(projectId)}/team/members/${memberId}`)
  return res.data
}

export async function inviteToProject(projectId: string, email: string, roles: string[]) {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/team/invite`, {
    email,
    roles,
  })
  return res.data as {
    success: boolean
    mode: 'added_existing' | 'invited'
    email_sent?: boolean
    invitation_id?: number
    member?: ProjectMemberInfo
  }
}

// Accept a project invitation as an ALREADY-registered, signed-in user. New
// users accept through /api/auth/register instead (the invitation token is
// consumed there); this is the path for everyone who already has an account —
// including a user in another organization, whose runtime database is
// provisioned inside this call. It never asks for anything: the organization
// is put on the default database the platform gives it, and can move the
// application into one of its own afterwards (see below).
export interface AcceptResult {
  success: boolean
  project_id?: string
  owner_user_id?: string
  cross_tenant?: boolean
  provisioned?: boolean
  redirect?: string
}

export async function acceptProjectInvitation(projectId: string, token: string) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectId)}/team/accept-invitation/${encodeURIComponent(token)}`,
  )
  return res.data as AcceptResult
}

// ---- your organization's own database -----------------------------------
// Self-served by a customer organization's func-admin from inside the running
// app: see which database this organization's copy runs in, move it into one
// the organization controls, or go back to the platform's default.

export interface TenantDatabaseBody {
  db_type: string
  host?: string
  port?: number
  database_name?: string
  oracle_connect_type?: string
  username?: string
  password?: string
  sqlite_path?: string
  db_schema?: string
  test_only?: boolean
}

export interface MyTenantDatabase {
  success: boolean
  /** Running on the database the platform provisioned (nothing supplied). */
  using_default: boolean
  db_name?: string | null
  provisioned_at?: string | null
  /** What a database of your own must be — the app's business logic needs it. */
  db_type: 'postgres' | 'oracle' | 'sqlite'
  /** What the default database is. Differs from db_type only when the default
   *  cannot run this application at all (e.g. an Oracle app), in which case
   *  supplying your own is required rather than optional. */
  default_db_type: 'postgres' | 'oracle' | 'sqlite'
  required_fields: Record<string, string[]>
  connection: (Omit<TenantDatabaseBody, 'password' | 'test_only'> & {
    has_password?: boolean
  }) | null
}

export interface TenantDatabaseResult {
  success: boolean
  tested?: boolean
  message?: string
  using_default?: boolean
  db_name?: string | null
  objects?: string[]
  failures?: string[]
}

export async function getMyTenantDatabase(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/my-tenant/database`)
  return res.data as MyTenantDatabase
}

// Tried, then deployed into, then stored — a switch that cannot complete
// leaves the organization running where it already is. `test_only` only tries.
export async function setMyTenantDatabase(projectId: string, body: TenantDatabaseBody) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectId)}/team/my-tenant/database`, body,
  )
  return res.data as TenantDatabaseResult
}

// Back to the platform's default database. The data in your own database is
// left where it is, not deleted.
export async function resetMyTenantDatabase(projectId: string) {
  const res = await api.delete(`/projects/${encodeURIComponent(projectId)}/team/my-tenant/database`)
  return res.data as TenantDatabaseResult
}

export async function cancelProjectInvitation(projectId: string, invitationId: number) {
  const res = await api.delete(
    `/projects/${encodeURIComponent(projectId)}/team/invitations/${invitationId}`,
  )
  return res.data
}

// ---- cross-tenant (customer organizations) ------------------------------
// The project owner grants the running application to OTHER organizations
// (tenants). Each customer tenant gets its own runtime database + a func-admin;
// func-admins self-administer their own tenant's runtime users.

export async function listProjectTenants(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/tenants`)
  return res.data as { success: boolean; tenants: ProjectTenantInfo[] }
}

export async function grantToTenant(projectId: string, email: string) {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/team/tenant-grants`, {
    email,
  })
  return res.data as {
    success: boolean
    tenant_id: string
    tenant: TenantInfo
    email_sent: boolean
    status: string
  }
}

export async function listMyTenantMembers(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/my-tenant/members`)
  return res.data as { success: boolean; tenant: TenantInfo; members: ProjectMemberInfo[] }
}

export async function inviteToMyTenant(projectId: string, email: string, roles: string[]) {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/team/my-tenant/invite`, {
    email,
    roles,
  })
  return res.data as {
    success: boolean
    mode: 'added_existing' | 'invited'
    email_sent?: boolean
    invitation_id?: number
    member?: ProjectMemberInfo
  }
}

// ---- functional (approver) roles ----------------------------------------
// Business/approver roles in the tenant data DB (twasta_user_roles), separate
// from the platform access roles above. FUNC-ADMIN is the super-approver role.

export async function listFunctionalRoles(
  projectId: string,
): Promise<{ success: boolean; roles: string[] }> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/team/functional-roles`)
  return res.data
}

export async function listFunctionalRoleUsers(
  projectId: string,
): Promise<{ success: boolean; users: FunctionalRoleUser[] }> {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectId)}/team/functional-roles/users`,
  )
  return res.data
}

export async function setUserFunctionalRoles(
  projectId: string, userId: string, roles: string[],
): Promise<{ success: boolean; user_id: string; functional_roles: string[] }> {
  const res = await api.put(
    `/projects/${encodeURIComponent(projectId)}/team/functional-roles/users/${encodeURIComponent(userId)}`,
    { roles },
  )
  return res.data
}
